import { Logger } from './logger';
const console = new Logger('CharacterSetPluginManager');
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function parseNode(value) {
    if (!isRecord(value) ||
        typeof value.id !== 'string' ||
        typeof value.label !== 'string' ||
        typeof value.selectable !== 'boolean') {
        return null;
    }
    const children = Array.isArray(value.children)
        ? value.children.map(parseNode)
        : undefined;
    if (children?.some((child) => child === null)) {
        return null;
    }
    return {
        id: value.id,
        label: value.label,
        selectable: value.selectable,
        children: children?.filter((child) => child !== null)
    };
}
function parseCoverageLevel(value) {
    if (!isRecord(value) ||
        !['essential', 'recommended', 'optional'].includes(String(value.id)) ||
        typeof value.label !== 'string' ||
        typeof value.default !== 'boolean') {
        return null;
    }
    return {
        id: value.id,
        label: value.label,
        default: value.default
    };
}
function parseProvider(value) {
    if (!isRecord(value) ||
        typeof value.id !== 'string' ||
        typeof value.name !== 'string' ||
        typeof value.version !== 'string' ||
        !Array.isArray(value.tree)) {
        return null;
    }
    const tree = value.tree.map(parseNode);
    if (tree.some((node) => node === null)) {
        return null;
    }
    const coverageLevels = Array.isArray(value.coverage_levels)
        ? value.coverage_levels.map(parseCoverageLevel)
        : [];
    if (coverageLevels.some((level) => level === null)) {
        return null;
    }
    return {
        id: value.id,
        name: value.name,
        version: value.version,
        coverageLevels: coverageLevels.filter((level) => level !== null),
        tree: tree.filter((node) => node !== null)
    };
}
/** Loads bundled Character Set provider wheels and exposes their query API. */
export class CharacterSetPluginManager {
    constructor() {
        this.providers = new Map();
        this.readyPromise = null;
    }
    async ensureReady() {
        if (this.readyPromise) {
            return this.readyPromise;
        }
        this.readyPromise = this.load().catch((error) => {
            this.readyPromise = null;
            throw error;
        });
        return this.readyPromise;
    }
    getProviders() {
        return [...this.providers.values()];
    }
    async getCharacters(providerId, setIds, levels) {
        await this.ensureReady();
        const pyodide = await this.waitForPyodide();
        const result = await pyodide.runPythonAsync(`
import json
from importlib.metadata import entry_points

provider_id = ${JSON.stringify(providerId)}
set_ids = ${JSON.stringify(setIds)}
levels = ${JSON.stringify(levels)}
result = None
for entry in entry_points(group='counterpunch_character_set_plugins'):
    provider = entry.load()()
    if provider.provider_id == provider_id:
        result = provider.characters(set_ids, levels)
        break
if result is None:
    raise RuntimeError(f'Character Set provider not installed: {provider_id}')
json.dumps(result)
`);
        return JSON.parse(result);
    }
    async load() {
        const pyodide = await this.waitForPyodide();
        const manifestResponse = await fetch('/wheels/wheels.json');
        if (!manifestResponse.ok) {
            throw new Error('Unable to load bundled Character Set wheels.');
        }
        const manifest = (await manifestResponse.json());
        const wheels = Array.isArray(manifest.wheels)
            ? manifest.wheels.filter((wheel) => typeof wheel === 'string' &&
                wheel.startsWith('counterpunch_'))
            : [];
        if (wheels.length) {
            await pyodide.loadPackage('micropip');
            await pyodide.runPythonAsync('import micropip');
            for (const wheel of wheels) {
                await pyodide.runPythonAsync(`await micropip.install(${JSON.stringify(`/wheels/${wheel}`)})`);
            }
        }
        const result = await pyodide.runPythonAsync(`
import json
from importlib.metadata import entry_points

json.dumps([
    entry.load()().metadata()
    for entry in entry_points(group='counterpunch_character_set_plugins')
])
`);
        for (const value of JSON.parse(result)) {
            const provider = parseProvider(value);
            if (provider) {
                this.providers.set(provider.id, provider);
            }
        }
    }
    async waitForPyodide() {
        const startedAt = performance.now();
        while (!window.pyodide) {
            if (performance.now() - startedAt > 30_000) {
                throw new Error('Timed out waiting for Python to initialize.');
            }
            await new Promise((resolve) => window.setTimeout(resolve, 50));
        }
        return window.pyodide;
    }
}
export const characterSetPluginManager = new CharacterSetPluginManager();
